/**
 * Personal share / download for generated evaluation reels (max rally, slow-mo).
 *
 * Instagram and TikTok do not let a web app post to a personal profile.
 * The reliable path is: hand the MP4 to the OS share sheet, or save it
 * and open the app so the player can pick it from Recents.
 */

export const SHARE_DESTINATIONS = [
  {
    id: "instagram",
    labelKey: "shareDestInstagram",
    defaultLabel: "Instagram",
    deepLink: "instagram://app",
    webLink: "https://www.instagram.com/",
  },
  {
    id: "tiktok",
    labelKey: "shareDestTiktok",
    defaultLabel: "TikTok",
    deepLink: "tiktok://",
    webLink: "https://www.tiktok.com/upload",
  },
  {
    id: "whatsapp",
    labelKey: "shareDestWhatsapp",
    defaultLabel: "WhatsApp",
    deepLink: "whatsapp://send",
    webLink: "https://web.whatsapp.com/",
  },
  {
    id: "facebook",
    labelKey: "shareDestFacebook",
    defaultLabel: "Facebook",
    deepLink: "fb://",
    webLink: "https://www.facebook.com/",
  },
  {
    id: "more",
    labelKey: "shareDestMore",
    defaultLabel: "More",
    deepLink: null,
    webLink: null,
  },
  {
    id: "download",
    labelKey: "shareDestDownload",
    defaultLabel: "Download",
    deepLink: null,
    webLink: null,
  },
];

export function filenameForReel(kind = "max_rally") {
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = kind === "stroke_slowmo" ? "slowmo" : "max-rally";
  return `SmartTT_${slug}_${stamp}.mp4`;
}

export function shareTextForReel(kind, t = (k, d) => d || k) {
  if (kind === "stroke_slowmo") {
    return t("shareReelCaptionSlowMo", "Slow-mo from my SmartTT session");
  }
  return t("shareReelCaption", "Check out my max rally on SmartTT");
}

export function blobToShareFile(blob, filename) {
  const type =
    blob?.type && String(blob.type).startsWith("video/")
      ? blob.type
      : "video/mp4";
  return new File([blob], filename, { type });
}

export function canShareFiles(file) {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    return false;
  }
  if (typeof navigator.canShare !== "function") return true;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

export function downloadBlob(blob, filename) {
  if (typeof document === "undefined" || !blob) return;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename || "SmartTT-reel.mp4";
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
}

function looksLikeObjectStorage(url) {
  return /s3\.|amazonaws\.com|ovh\.net|storage\.googleapis\.com/i.test(
    String(url || "")
  );
}

export async function fetchVideoBlob(url, { signal } = {}) {
  const target = String(url || "").trim();
  if (!target) throw new Error("Missing video");

  if (target.startsWith("blob:")) {
    const local = await fetch(target, { signal });
    if (!local.ok) throw new Error("Could not read clip");
    return local.blob();
  }

  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const headers = {};
  if (token && !looksLikeObjectStorage(target)) {
    headers.Authorization = token;
  }

  try {
    const response = await fetch(target, {
      signal,
      credentials: "omit",
      headers,
    });
    if (response.ok) return response.blob();
  } catch {
    /* CORS or network — try without auth headers */
  }

  const fallback = await fetch(target, { signal, credentials: "omit" });
  if (!fallback.ok) {
    throw new Error("Could not download clip");
  }
  return fallback.blob();
}

export function isLikelyMobileShare() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
}

export function openExternalApp(deepLink, webLink) {
  if (typeof window === "undefined") return;
  const mobile = isLikelyMobileShare();
  if (mobile && deepLink) {
    window.location.href = deepLink;
    if (webLink) {
      window.setTimeout(() => {
        window.open(webLink, "_blank", "noopener,noreferrer");
      }, 1100);
    }
    return;
  }
  if (webLink) {
    window.open(webLink, "_blank", "noopener,noreferrer");
  }
}

/**
 * @returns {Promise<{ action: 'shared' | 'shared_text' | 'downloaded' | 'downloaded_then_open' | 'cancelled' }>}
 */
export async function shareReelToDestination({
  destination,
  blob,
  filename,
  title,
  text,
}) {
  if (!blob) throw new Error("Missing clip");
  const file = blobToShareFile(blob, filename || "SmartTT-reel.mp4");
  const dest =
    SHARE_DESTINATIONS.find((item) => item.id === destination) || null;

  if (destination === "download") {
    downloadBlob(blob, file.name);
    return { action: "downloaded" };
  }

  if (canShareFiles(file)) {
    try {
      await navigator.share({
        files: [file],
        title: title || file.name,
        text: text || "",
      });
      return { action: "shared" };
    } catch (err) {
      if (err?.name === "AbortError") return { action: "cancelled" };
    }
  }

  if (
    destination === "more" &&
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({ title: title || file.name, text: text || "" });
      return { action: "shared_text" };
    } catch (err) {
      if (err?.name === "AbortError") return { action: "cancelled" };
    }
  }

  downloadBlob(blob, file.name);
  if (dest?.deepLink || dest?.webLink) {
    window.setTimeout(() => openExternalApp(dest.deepLink, dest.webLink), 350);
    return { action: "downloaded_then_open" };
  }
  return { action: "downloaded" };
}
